"use strict";

const InputBaseControlTextfieldComponent = require("@gluenetworks/input_base_control").Textfield;
const Renderer = require("./InputTextControlRenderer");
const _ = require("lodash");
const DEFAULT_PATTERN_VIOLATION_MESSAGE = "Please enter a value matching pattern : ";
const DEFAULT_REQUIRED_VIOLATION_MESSAGE = "This is a required field";

class InputTextControlComponent extends InputBaseControlTextfieldComponent {
    /**
     * @schema.name Input Text Control
     * @schema.package @gluenetworks/input_text_control
     * @schema.namespace require("@gluenetworks/steve_ui_core").Control.Textfield.InputText
     * @schema.events ["Disabled", "Value", "Visible", "Invalid", "Pattern", "PatternMatchAll", "Placeholder", "DarkTheme", "Focus", "Blur", "SetAttribute",
     *          "UserValue", "UserFocus", "UserBlur", "UserKeyDown", "AutocompleteHint", "Title", "Required", "ValidationVerbiage"]
     *
     * @schema.configuration.autocompleteHint.name autocompleteHint
     * @schema.configuration.autocompleteHint.type string
     * @schema.configuration.autocompleteHint.type string
     * @schema.configuration.autocompleteHint.default "off"
     * @schema.configuration.autocompleteHint.required false
     *
     * @schema.configuration.patternMatchAll.name patternMatchAll
     * @schema.configuration.patternMatchAll.type boolean
     * @schema.configuration.patternMatchAll.default false
     * @schema.configuration.patternMatchAll.required false
     *
     * @schema.configuration.pattern.name pattern
     * @schema.configuration.pattern.type string
     * @schema.configuration.pattern.default undefined
     * @schema.configuration.pattern.required false
     *
     * @schema.configuration.useNativeValidation.name useNativeValidation
     * @schema.configuration.useNativeValidation.type boolean
     * @schema.configuration.useNativeValidation.default false
     * @schema.configuration.useNativeValidation.required false
     *
     * @schema.configuration.validationVerbiage.name validationVerbiage
     * @schema.configuration.validationVerbiage.type object
     * @schema.configuration.validationVerbiage.default null
     * @schema.configuration.validationVerbiage.required false
     */

    constructor(configOptions) {
        super(configOptions);
        this._autocompleteHint = _.get(configOptions, "autocompleteHint", "off");
        // determine if we need fully pattern matching, or not
        this._patternMatchAll = _.get(configOptions, "patternMatchAll", false);
        this._pattern = _.get(configOptions, "pattern", undefined);
        this._showErrorMessage = _.get(configOptions, "showErrorMessage", true);
        this._useNativeValidation = _.get(configOptions, "useNativeValidation", false);
        let validationVerbiage = _.get(configOptions, "validationVerbiage", {});
        this._validationVerbiage = this._buildValidationVerbiage(validationVerbiage);
        this._renderer = new Renderer(this);

        this._registerListeners();
    }
    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS */
    /*******************************************/
    _registerListeners() {}
    _unregisterListeners() {}
    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS */
    /* START PUBLIC COMPONENT MODEL METHODS */
    /*******************************************/
    get useNativeValidation() {
        return this._useNativeValidation;
    }

    get showErrorMessage() {
        return this._showErrorMessage;
    }

    get renderer() {
        return this.getRenderer();
    }
    getRenderer() {
        return this._renderer;
    }
    shutdown() {
        return this._renderer._shutdown().then(() => {
            this._renderer = undefined;
            this._disabled = undefined;
            this._invalid = undefined;
            this._visible = undefined;
            this._value = undefined;
            this._darkTheme = undefined;
            this._theme = undefined;
            this._placeholder = undefined;
            this._autocompleteHint = undefined;
            this._patternMatchAll = undefined;
            this._pattern = undefined;
            this._useNativeValidation = undefined;
            this._showErrorMessage = undefined;
            this._required = undefined;
            this._validationVerbiage = undefined;
            this._DEFAULT_PLACEHOLDER = undefined;
        });
    }
    /*******************************************/
    /* END COMPONENT MODEL METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE METHODS */
    /*******************************************/

    /**
     * Verifies if a given string is a valid regular expression
     * @param val
     * @returns {boolean} - true if valid else false
     * @private
     */
    _isRegexValid(val) {
        let isValid = true;
        try {
            new RegExp(val);
        } catch (e) {
            isValid = false;
        }
        return isValid;
    }

    /**
     * Returns validation messages verbiage
     * @param validationVerbiage
     * @returns {any}
     * @private
     */
    _buildValidationVerbiage(validationVerbiage) {
        let defaultValidationVerbiage = {};
        // default pattern violation message verbiage makes use of pattern value, hence we set it only if pattern attribute is set
        if (this._pattern) {
            defaultValidationVerbiage["pattern"] = DEFAULT_PATTERN_VIOLATION_MESSAGE + this.pattern;
        }
        defaultValidationVerbiage["required"] = DEFAULT_REQUIRED_VIOLATION_MESSAGE;
        return _.isEmpty(validationVerbiage)
            ? defaultValidationVerbiage
            : Object.assign(defaultValidationVerbiage, validationVerbiage);
    }

    /*******************************************/
    /* END PRIVATE METHODS */
    /* START PUBLIC API METHODS */
    /*******************************************/
    /**
     * @schema.methods.setAutocompleteHint.name setAutocompleteHint
     * @schema.methods.setAutocompleteHint.description Sets the autocomplete property on the control and returns an instance of the control for chainability
     * @schema.methods.setAutocompleteHint.returns Instance of the control
     * @schema.methods.setAutocompleteHint.parameters.newVal.name newVal
     * @schema.methods.setAutocompleteHint.parameters.newVal.description The new autocompleteHint value
     * @schema.methods.setAutocompleteHint.parameters.newVal.type string
     */
    setAutocompleteHint(newVal) {
        let autocompleteHintNewVal = newVal ? newVal : "";
        if (this._autocompleteHint !== autocompleteHintNewVal) {
            this._autocompleteHint = autocompleteHintNewVal;
            this.emit("AutocompleteHint", autocompleteHintNewVal);
        }
        return this;
    }

    /**
     * @schema.methods.getAutocompleteHint.name getAutocompleteHint
     * @schema.methods.getAutocompleteHint.description Returns the autocompleteHint property
     * @schema.methods.getAutocompleteHint.returns string
     */
    getAutocompleteHint() {
        return this._autocompleteHint;
    }

    get autocompleteHint() {
        return this.getAutocompleteHint();
    }

    set autocompleteHint(newVal) {
        this.setAutocompleteHint(newVal);
    }

    /**
     * @schema.methods.setPatternMatchAll.name setPatternMatchAll
     * @schema.methods.setPatternMatchAll.description Decides if given pattern must match all of input text
     * @schema.methods.setPatternMatchAll.parameters.newVal.name newVal
     * @schema.methods.setPatternMatchAll.parameters.newVal.description new patternMatchAll value
     * @schema.methods.setPatternMatchAll.parameters.newVal.type boolean
     * @schema.methods.setPatternMatchAll.returns Instance of the control
     */
    setPatternMatchAll(newVal) {
        if (this.patternMatchAll !== newVal) {
            this._patternMatchAll = newVal === true;
            this.emit("PatternMatchAll", this.patternMatchAll);
        }
        return this;
    }

    /**
     * @schema.methods.getPatternMatchAll.name getPatternMatchAll
     * @schema.methods.getPatternMatchAll.description Returns boolean value indicating if given pattern must match all of input text
     * @schema.methods.getPatternMatchAll.returns boolean
     */
    getPatternMatchAll() {
        return this._patternMatchAll;
    }

    get patternMatchAll() {
        return this.getPatternMatchAll();
    }

    set patternMatchAll(newVal) {
        this.setPatternMatchAll(newVal);
    }

    /**
     * @schema.methods.setPattern.name setPattern
     * @schema.methods.setPattern.description Set regular expression pattern to enable validation of input text
     * @schema.methods.setPattern.parameters.newVal.name newVal
     * @schema.methods.setPattern.parameters.newVal.description new regular expression pattern value
     * @schema.methods.setPattern.parameters.newVal.type string
     * @schema.methods.setPattern.returns Instance of the control
     */
    setPattern(newVal) {
        if (newVal && this._isRegexValid(newVal) && newVal !== this._pattern) {
            this._pattern = newVal;
            if (!this._validationVerbiage.pattern) {
                this._validationVerbiage.pattern =
                    DEFAULT_PATTERN_VIOLATION_MESSAGE + this._pattern;
            }
        } else {
            this._pattern = null;
        }
        this.emit("Pattern", this._pattern);
        return this;
    }

    /**
     * @schema.methods.getPattern.name getPattern
     * @schema.methods.getPattern.description Returns regular expression pattern
     * @schema.methods.getPattern.returns string
     * @returns {*}
     */
    getPattern() {
        return this._pattern;
    }

    get pattern() {
        return this.getPattern();
    }

    set pattern(newVal) {
        this.setPattern(newVal);
    }

    /**
     * @schema.methods.getValidationMessages.name getValidationMessages
     * @schema.methods.getValidationMessages.description Returns list of validation violations. For Example: returns validation messages when the text value doesn't
     *          match given regular expression (pattern) or is left blank when it is required
     * @schema.methods.getValidationMessages.returns {Array}
     */
    getValidationMessages() {
        return this._renderer.getViolations().slice(0);
    }

    get validationMessages() {
        return this.getValidationMessages();
    }

    /**
     * @schema.methods.setValidationVerbiage.name setValidationVerbiage
     * @schema.methods.setValidationVerbiage.description Changes the validation messages verbiage displayed in case of validation
     *                          errors on this control. For example control value doesn't match pattern or is left blank when it is required
     * @schema.methods.setValidationVerbiage.parameters.validationVerbiage.name validationVerbiage
     * @schema.methods.setValidationVerbiage.parameters.validationVerbiage.description validation messages displayed on the control when there are
     *                          validation errors on the control value
     * @schema.methods.setValidationVerbiage.parameters.validationVerbiage.type object
     * @schema.methods.setValidationVerbiage.returns Instance of the control
     * @returns {*}
     */
    setValidationVerbiage(validationVerbiage) {
        if (
            _.isEmpty(validationVerbiage) ||
            _.isEqual(this._validationVerbiage, validationVerbiage)
        ) {
            return;
        }
        Object.assign(this._validationVerbiage, validationVerbiage);
        this.emit("ValidationVerbiage", this._validationVerbiage);
        return this;
    }

    /**
     * @schema.methods.getValidationVerbiage.name getValidationVerbiage
     * @schema.methods.getValidationVerbiage.description Returns existing validation message verbiage
     * @schema.methods.getValidationVerbiage.returns {object}
     */
    getValidationVerbiage() {
        return this._validationVerbiage;
    }

    get validationVerbiage() {
        return this._validationVerbiage;
    }

    set validationVerbiage(validationVerbiage) {
        this.setValidationVerbiage(validationVerbiage);
    }

    /*******************************************/
    /* END PUBLIC API METHODS */
    /*******************************************/
}

module.exports = InputTextControlComponent;
